import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImpactFactorTrendChart } from '@/features/journal/components/charts/ImpactFactorTrendChart';
import { ResearcherCountChart } from '@/features/researcher/components/charts/ResearcherCountChart';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

/**
 * ResearcherGraphs
 * - Presentational component extracted from Researchers.jsx graph section.
 * - Keeps exact markup/classes and rmVars asset paths.
 */
export default function ResearcherGraphs({ graphExpanded: externalGraphExpanded, setGraphExpanded: externalSetGraphExpanded }) {
  // API에서 데이터 가져오기 (출판물 트렌드)
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendErr, setTrendErr] = useState(null);

  // 연구자수 차트 데이터
  const [researcherCountData, setResearcherCountData] = useState(null);
  const [researcherCountLoading, setResearcherCountLoading] = useState(false);
  const [researcherCountErr, setResearcherCountErr] = useState(null);

  // 세 가지 카테고리 데이터
  const [researcherData, setResearcherData] = useState({
    worldTop2Scientist: [],
    highlyCitedResearchers: [],
    ourOutstandingResearchers: []
  });
  const [researcherLoading, setResearcherLoading] = useState(false);
  const [researcherErr, setResearcherErr] = useState(null);

  // 슬라이더 상태
  const [slideIndex, setSlideIndex] = useState({
    worldTop2Scientist: 0,
    highlyCitedResearchers: 0,
    ourOutstandingResearchers: 0
  });
  // graph toggle state - prop이 있으면 prop 사용, 없으면 내부 상태 사용
  const [internalGraphExpanded, setInternalGraphExpanded] = useState(true);
  const graphExpanded = externalGraphExpanded !== undefined ? externalGraphExpanded : internalGraphExpanded;
  const setGraphExpanded = externalSetGraphExpanded || setInternalGraphExpanded;

  useEffect(() => {
    let alive = true;
    async function fetchTrend() {
      setTrendLoading(true);
      setTrendErr(null);
      try {
        const response = await get('/rm/v1/researcher/publications_trend');
        if (alive) {
          if (response?.status && response?.result) {
            setTrendData(response.result);
          } else {
            setTrendErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setTrendData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setTrendErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setTrendLoading(false);
        }
      }
    }
    fetchTrend();
    return () => { alive = false; };
  }, []);

  // 연구자수 차트 데이터 가져오기
  useEffect(() => {
    let alive = true;
    async function fetchResearcherCount() {
      setResearcherCountLoading(true);
      setResearcherCountErr(null);
      try {
        const response = await get('/rm/v1/researcher/count_by_year');
        if (alive) {
          if (response?.status && response?.result) {
            setResearcherCountData(response.result);
            setResearcherCountErr(null);
          } else {
            // 데이터가 없을 때는 에러가 아니라 빈 데이터로 처리 (로딩은 계속 표시)
            setResearcherCountData(null);
            // 실제 네트워크 에러나 서버 에러일 때만 에러 메시지 표시
            setResearcherCountErr(null);
          }
        }
      } catch (e) {
        if (alive) {
          // 네트워크 에러나 실제 API 에러일 때만 에러 메시지 표시
          const errorMessage = e?.response?.data?.message || e?.message;
          // 404나 데이터 없음은 에러가 아님
          if (e?.response?.status === 404 || errorMessage?.includes('not found')) {
            setResearcherCountErr(null);
            setResearcherCountData(null);
          } else {
            setResearcherCountErr(errorMessage);
          }
        }
      } finally {
        if (alive) {
          setResearcherCountLoading(false);
        }
      }
    }
    fetchResearcherCount();
    return () => { alive = false; };
  }, []);

  // 연구자 데이터 가져오기 (세 가지 카테고리)
  useEffect(() => {
    let alive = true;
    async function fetchResearchers() {
      setResearcherLoading(true);
      setResearcherErr(null);
      try {
        const data = await get('/rm/v1/resource_highlights');

        if (alive) {
          const result = data?.result || data;

          setResearcherData({
            worldTop2Scientist: Array.isArray(result?.world_top_2_scientist) ? result.world_top_2_scientist : [],
            highlyCitedResearchers: Array.isArray(result?.highly_cited_researchers) ? result.highly_cited_researchers : [],
            ourOutstandingResearchers: Array.isArray(result?.our_outstanding_researchers) ? result.our_outstanding_researchers : []
          });
        }
      } catch (e) {
        if (alive) {
          setResearcherErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setResearcherLoading(false);
        }
      }
    }
    fetchResearchers();
    return () => { alive = false; };
  }, []);

  // 데이터 변환: API 데이터를 series 형식으로 변환 (출판물 트렌드)
  const trendSeries = useMemo(() => {
    if (!trendData || typeof trendData !== 'object') {
      return null;
    }

    // impact_factor_graph 형식의 데이터를 series 형식으로 변환
    const mapSeries = (label, color, arr) => {
      const rows = Array.isArray(arr) ? arr : [];
      const data = rows
        .filter(d => d && typeof d.indicator_year === 'number' && d.indicator_year > 0 && typeof d.impact !== 'undefined' && d.impact !== null && !isNaN(Number(d.impact)))
        .map(d => ({ year: d.indicator_year, value: Number(d.impact) }))
        .sort((a, b) => a.year - b.year);
      if (!data.length) return null;
      return { label, color, data };
    };

    const sci = mapSeries('SCI', '#B298F2', trendData.SCI);
    const scopus = mapSeries('Scopus', '#FFBE8D', trendData.Scopus);
    const kci = mapSeries('KCI', '#5FA3E0', trendData.KCI);
    return [sci, scopus, kci].filter(Boolean);
  }, [trendData]);

  // 연구자수 차트 데이터 변환
  const researcherCountSeries = useMemo(() => {
    if (!researcherCountData || typeof researcherCountData !== 'object') {
      return null;
    }

    // API 응답 데이터를 series 형식으로 변환
    const mapSeries = (label, color, arr) => {
      const rows = Array.isArray(arr) ? arr : [];
      const data = rows
        .filter(d => d && typeof d.indicator_year === 'number' && d.indicator_year > 0 && typeof d.impact !== 'undefined' && d.impact !== null && !isNaN(Number(d.impact)))
        .map(d => ({ year: d.indicator_year, value: Number(d.impact) }))
        .sort((a, b) => a.year - b.year);
      if (!data.length) return null;
      return { label, color, data };
    };

    const sci = mapSeries('SCIs', '#B298F2', researcherCountData.SCIs);
    const scopus = mapSeries('Scopus', '#FFBE8D', researcherCountData.Scopus);
    const kci = mapSeries('KCI', '#5FA3E0', researcherCountData.KCI);
    return [sci, scopus, kci].filter(Boolean);
  }, [researcherCountData]);

  // ResearcherCard와 동일한 데이터 매핑
  const formatResearcherData = (item) => {
    const profileSrc = item?.profile_pic && String(item.profile_pic).trim() !== ''
      ? item.profile_pic
      : `${rmVars.themeUrl}images/profile03.png`;

    // const nameText = item?.scholar_name || item?.name || '—';
    const nameText = (() => {
      const eng = item?.scholar_name_eng;
      const kor = item?.scholar_name_kor;
      const name = item?.scholar_name;

      if (eng && kor) {
        return `${eng}(${kor})`;
      } else if (eng && !kor) {
        // eng 있고 kor 없을 때 scholar_name이 있으면 괄호에 넣고, 없으면 eng만 표시
        return name ? `${eng}(${name})` : eng;
      } else if (!eng && kor) {
        return kor;
      } else if (!eng && !kor && name) {
        return name;
      }

      return item?.name || '—';
    })();

    const dept = item?.dept_name || item?.maj_name || '';
    const college = item?.col_name || '';
    const subText = [dept, college].filter(v => v && String(v).trim() !== '').join(' · ');

    // const docCount = item?.doc_count ?? item?.scholarly_output ?? item?.publication_count ?? '—';
    // const citeCount = item?.citation_count ?? item?.citedby_count ?? item?.citations ?? '—';
    // const hIndex = item?.h_index ?? item?.hindex ?? '—';

    let totalCount = 0;
    let totalCitation = 0;
    let yearData = { start: null, end: null, maxBar: 0, data: [] }; // 초기값 설정
    if (item?.publications_by_year) {
      const pby = item.publications_by_year;

      // 1. yearRange 저장
      yearData.start = pby.start_year;
      yearData.end = pby.end_year;
      yearData.data = pby.data || [];

      // 2. 합계 계산 (data가 배열인 경우)
      if (Array.isArray(pby.data)) {
        yearData.maxBar = pby.data.length;
        const totals = pby.data.reduce((acc, curr) => {
          acc.citationTotal += parseInt(curr.citation || 0, 10);
          acc.countTotal += parseInt(curr.count || 0, 10);
          return acc;
        }, { citationTotal: 0, countTotal: 0 });

        totalCitation = totals.citationTotal;
        totalCount = totals.countTotal;
      }
    }
    const docCount = (totalCount || item?.resource_count || 0).toLocaleString();
    const citeCount = totalCitation.toLocaleString();
    const hIndex = item?.meta?.hindex || 0;

    return {
      profileSrc,
      nameText,
      subText,
      docCount,
      citeCount,
      hIndex
    };
  };

  // 슬라이더 컴포넌트
  const ResearcherSlider = ({ category, data, title, subtitle }) => {
    const navigate = useNavigate();
    const items = Array.isArray(data) ? data : [];
    const currentIndex = slideIndex[category];
    const currentItem = items[currentIndex] || null;
    const formatted = currentItem ? formatResearcherData(currentItem) : null;
    const totalSlides = items.length;

    // 연구자 ID 가져오기
    const researcherId = currentItem?.scholar_id || currentItem?.id || currentItem?.researcher_id;

    // 연구자 상세 페이지로 이동
    const handleResearcherClick = useCallback((e) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      if (!researcherId) return;
      navigate(`/researchers/${encodeURIComponent(researcherId)}`);
      try {
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
          setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
        }
      } catch (e) {
        // no-op
      }
    }, [researcherId, navigate]);

    const goToSlide = (index) => {
      setSlideIndex(prev => ({ ...prev, [category]: index }));
    };

    const nextSlide = () => {
      setSlideIndex(prev => ({
        ...prev,
        [category]: (prev[category] + 1) % totalSlides
      }));
    };

    const prevSlide = () => {
      setSlideIndex(prev => ({
        ...prev,
        [category]: (prev[category] - 1 + totalSlides) % totalSlides
      }));
    };

    return (
      <div className="p-5 border border-[#E4E8F1] rounded-[12px] lg:bg-[#F3F5F9] xl:w-[calc((100%-42px)/3)] xl:px-5 xl:pt-8 xl:pb-5">
        <div className="relative">
          <h3 className="pr-[45px] text-[15px] font-bold leading-[130%] text-[#6C7687] xl:text-[16px] xl:pr-[55px]">
            {title}
          </h3>
          <p className="pr-[45px] text-[12px] text-[#8D9AAF] leading-[140%] mt-1 xl:text-[14px] xl:pr-[55px]">
            {subtitle}
          </p>
          <div className="absolute top-0 right-0 xl:top-[-12px] xl:right-[-10px]">
            <img src={`${rmVars.themeUrl}images/badge.png`} className="w-[37px] xl:w-[59px]" alt="medal badge" />
          </div>
        </div>

        <div className="slider relative overflow-hidden w-full mx-auto border-t border-t-[#E4E8F1] pt-5 mt-[14px]">
          {researcherLoading ? (
            <div className="relative min-h-[200px]">
              <ChartSkeletonLoader show={researcherLoading} />
            </div>
          ) : researcherErr ? (
            <div className="text-center text-[14px] text-red-600 py-8">{String(researcherErr)}</div>
          ) : items.length === 0 ? (
            <div className="text-center text-[14px] text-[#8D9AAF] py-8">No data available</div>
          ) : formatted ? (
            <>
              <div className="slides flex transition-transform duration-300 ease-in-out">
                <div className="slide flex-shrink-0 w-full">
                  <div>
                    <div
                      className="w-[72px] h-[72px] overflow-hidden rounded-full border border-[#E4E8F1] mx-auto mb-[10px] cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={handleResearcherClick}
                    >
                      <img src={formatted.profileSrc} alt="photo" className="w-full h-full object-cover" />
                    </div>

                    <div
                      className="text-center text-[15px] text-[#376BDB] font-bold leading-[140%] xl:text-[16px] tracking-[-0.05em] cursor-pointer hover:underline"
                      onClick={handleResearcherClick}
                    >
                      {formatted.nameText}
                    </div>
                    <p className="mt-[5px] text-[12px] text-[#8D9AAF] leading-[140%] text-center xl:text-[14px]">
                      {formatted.subText || '\u00A0'}
                    </p>

                    <ul className="mt-[14px] px-[16px] border border-[#E4E8F1] rounded-[12px] text-[14px] text-[#6C7687] font-bold leading-[140%] bg-white xl:mt-4">
                      <li className="flex justify-between items-center py-[10px] border-b border-b-[#E4E8F1] xl:py-[14px]">
                        <span>Scholarly Output</span>
                        {formatted.docCount ? (
                          <a href={`${rmVars.homeUrl}/publications/?scholar_id=${encodeURIComponent(data[currentIndex]?.scholar_id)}`} className="hover:underline underline-offset-2 decoration-1">
                            <span className="text-[#376BDB]">{formatted.docCount}</span>
                          </a>
                        ) : (
                          <span className="text-[#333]">—</span>
                        )}
                      </li>
                      <li className="flex justify-between items-center py-[10px] border-b border-b-[#E4E8F1] xl:py-[14px]">
                        <span>Citation Count</span>
                        <span className="text-[#333]">{formatted.citeCount}</span>
                      </li>
                      <li className="flex justify-between items-center py-[10px] xl:py-[14px]">
                        <span>H-Index</span>
                        <span className="text-[#333]">{formatted.hIndex}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {totalSlides > 1 && (
                <div className="slide-controls flex justify-center items-center gap-2 mt-3">
                  <button
                    onClick={prevSlide}
                    className="w-[18px] h-[18px] text-[0px] bg-[#E4E8F1] text-[#333] rounded-full disabled:bg-[#ADB6C7]"
                    disabled={totalSlides <= 1}
                  >
                    prev
                  </button>
                  <div className="paging flex gap-1">
                    {items.slice(0, Math.min(totalSlides, 3)).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => goToSlide(idx)}
                        className={`w-[6px] h-[6px] text-[0px] rounded-full ${currentIndex === idx ? 'bg-[#333]' : 'bg-[#E4E8F1]'}`}
                      >
                        page{idx + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={nextSlide}
                    className="w-[18px] h-[18px] text-[0px] bg-[#E4E8F1] text-[#333] rounded-full disabled:bg-[#ADB6C7]"
                    disabled={totalSlides <= 1}
                  >
                    next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-[14px] text-[#8D9AAF] py-8">No data available</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="journal-graph mt-[18px] bg-white rounded-xl border border-[#E4E8F1] lg:mt-[37px]">
      <div
        className="journal-graph-toggle relative pt-[24px] pb-6 px-[20px] cursor-pointer lg:px-[25px] lg:pb-5 lg:pt-[20px]"
        onClick={() => setGraphExpanded(!graphExpanded)}
      >
        <div className="flex items-center gap-3 pr-[30px] text-[15px] font-bold leading-[130%] lg:text-[18px] lg:gap-[6px]">
          <div className="flex shrink-0 justify-center items-center w-[38px] h-[38px] rounded-lg bg-[#E4E8F1] lg:w-auto lg:h-auto lg:bg-transparent">
            <img src={`${rmVars.themeUrl}images/bar_chart_ico.png`} className="w-[22px]" alt="chart icon" />
          </div>
          Exploring Our Researcher
        </div>
        <img
          src={`${rmVars.themeUrl}images/expand_more_333.png`}
          className={`absolute right-[20px] top-[50%] w-[20px] translate-y-[-50%] transition-transform duration-300 lg:w-[24px] ${graphExpanded ? 'rotate-180' : ''}`}
          alt="toggle"
        />
      </div>

      {graphExpanded && (
        <div className="journal-graph-cont border-t border-t-[#E4E8F1] pt-5 px-[20px] lg:pt-[32px] lg:flex flex-wrap gap-[20px] lg:px-8 lg:pb-6" >
          <div className="chart-area w-full p-5 border border-[#E4E8F1] rounded-xl mb-[20px] lg:bg-[#F3F5F9] lg:p-8 lg:mb-0" >
            <h3 className="mb-4 text-[15px] font-bold leading-[130%] text-[#6C7687] text-center lg:text-[16px] lg:mb-6" >
              Number of Researchers with Publications Indexed in SCI, Scopus, and KCI
            </h3>

            <div className="min-h-[320px] relative">
              <ResearcherCountChart
                height={320}
                series={researcherCountSeries}
                loading={researcherCountLoading}
                error={researcherCountErr}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full xl:flex-row xl:gap-[21px]">
            <ResearcherSlider
              category="worldTop2Scientist"
              data={researcherData.worldTop2Scientist}
              title={
                <>
                  <span className="text-[#F3652D]">World Top 2%</span>
                  {' '}Scientist
                </>
              }
              subtitle="Based on Scopus data"
            />

            <ResearcherSlider
              category="highlyCitedResearchers"
              data={researcherData.highlyCitedResearchers}
              title={
                <>
                  Highly Cited
                  {' '}<span className="text-[#F3652D]"> Researchers</span>
                </>
              }
              subtitle="Based on Clarivate data"
            />

            <ResearcherSlider
              category="ourOutstandingResearchers"
              data={researcherData.ourOutstandingResearchers}
              title={
                <>
                  Our <span className="text-[#F3652D]">Outstanding</span>
                  {' '}Researchers
                </>
              }
              subtitle="Based on H-index"
            />
          </div>

          <div className="flex justify-center pb-5 mt-5 lg:hidden">
            <button
              className="flex items-center gap-[2px] px-[10px] py-1 text-[13px] font-semibold text-[#8D9AAF] cursor-pointer"
              onClick={() => setGraphExpanded(false)}
            >
              <img src={`${rmVars.themeUrl}images/expand_more_8D9AAF.png`} className="w-[14px] rotate-180" alt="hide" />
              Hide Chart
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
